import { ref } from 'vue';
import { useEnvProps } from '~/composables/useEnvProps';
import { usePageData } from '~/composables/usePageData';
import { ACCEPTABLE_VIDEO_FORMATS, ACCEPTABLE_VIDEO_FORMATS_UI, VIDEO_LIMITS, ONE_MB } from '../components/writeReview/constants';

type ReviewUploadedVideo = {
  url: string;
  thumbnailUrl?: string;
  isLoading: boolean;
  error: null;
};

const useReviewVideoUpload = ({ productId }: { productId: string }) => {
  const { hostlink } = useEnvProps();
  const { deviceType } = usePageData();
  const video = ref<ReviewUploadedVideo | null>(null);
  const isLoading = ref(false);
  const mediaErrorMessage = ref('');

  const uploadVideo = async (videoFile: File) => {
    isLoading.value = true;
    mediaErrorMessage.value = '';

    try {
      const formData = new FormData();
      formData.append('data', videoFile);

      const response = await fetch(`${hostlink}/xapi/digital/v1/product/${productId}/review/videoupload?_deviceType=${deviceType}`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok || data.review?.hasErrors) {
        throw new Error(data.review?.errors?.[0]?.message);
      } else {
        video.value = {
          url: data.review?.video?.videoUrl || '',
          thumbnailUrl: data.review?.video?.videoThumbnailUrl || '',
          isLoading: false,
          error: null,
        };
      }
    } catch {
      mediaErrorMessage.value = 'Something went wrong. Please try again.';
    } finally {
      isLoading.value = false;
    }
  };

  const removeVideo = async () => {
    const isConfirmed = window.confirm('Are you sure you want to remove this video?');
    if (isConfirmed) {
      video.value = null;
      mediaErrorMessage.value = '';
    }
  };

  const validateVideo = async (file: File) => {
    const type = file.name.toLowerCase().split('.').pop();
    if (!type || !ACCEPTABLE_VIDEO_FORMATS.some((format) => format === type)) {
      mediaErrorMessage.value = `Video could not be uploaded. Please ensure the video is in one of the following formats: ${ACCEPTABLE_VIDEO_FORMATS_UI}`;
      return false;
    }
    if (file.size > VIDEO_LIMITS.VIDEO_SIZE_LIMIT * ONE_MB) {
      mediaErrorMessage.value = `Video is too large. File size must be ${VIDEO_LIMITS.VIDEO_SIZE_LIMIT} MB or less.`;
      return false;
    }
    return true;
  };

  return {
    video,
    isLoading,
    mediaErrorMessage,
    uploadVideo,
    removeVideo,
    validateVideo,
  };
};

export default useReviewVideoUpload;
